/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import { buildApiUrl, makeAuthenticatedRequest } from '../config/api';

const LEGAL_BASE_PATH = '/api/legal/legal-documents';

const normalizeDocumentType = (type = '') => {
  const safeType = type.toLowerCase().trim();
  if (safeType.includes('privacy')) {
    return 'privacy-policy';
  }
  if (safeType.includes('terms')) {
    return 'terms-of-service';
  }
  return type;
};

const withCacheBuster = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}ts=${Date.now()}`;
};

const PAGE_METADATA_PATH = '/api/legal/page-metadata';

export const legalApi = {
  /**
   * Fetch last-modified dates for static legal pages (based on file edit time).
   * Returns { 'cookie-policy': string|null, 'terms-of-service': string|null, 'privacy-policy': string|null }
   */
  async fetchPageMetadata() {
    const response = await fetch(
      buildApiUrl(withCacheBuster(PAGE_METADATA_PATH)),
      { cache: 'no-store' }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch page metadata');
    }
    return response.json();
  },

  async fetchAllPublic() {
    const response = await fetch(
      buildApiUrl(withCacheBuster(LEGAL_BASE_PATH)),
      { cache: 'no-store' }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch legal documents');
    }
    return response.json();
  },

  async fetchPublicDocument(type) {
    const normalizedType = normalizeDocumentType(type);
    const response = await fetch(
      buildApiUrl(withCacheBuster(`${LEGAL_BASE_PATH}/${normalizedType}`)),
      { cache: 'no-store' }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch legal document');
    }
    return response.json();
  },

  async fetchAdminDocuments() {
    const response = await makeAuthenticatedRequest(
      buildApiUrl(withCacheBuster(LEGAL_BASE_PATH)),
      {
        cache: 'no-store',
      }
    );
    if (!response) {
      throw new Error('Failed to fetch legal documents for admin');
    }

    if (!response.ok) {
      let details = '';
      try {
        const bodyText = await response.clone().text();
        if (bodyText) {
          details = ` — ${bodyText.slice(0, 160)}`;
        }
      } catch (err) {
        // ignore errors parsing body
      }
      throw new Error(
        `Failed to fetch legal documents for admin (status ${response.status}${
          response.statusText ? ` ${response.statusText}` : ''
        })${details}`
      );
    }
    return response.json();
  },

  async updateDocument(type, payload) {
    const normalizedType = normalizeDocumentType(type);
    const response = await makeAuthenticatedRequest(
      buildApiUrl(`${LEGAL_BASE_PATH}/${normalizedType}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          type: normalizedType,
        }),
      }
    );

    if (!response || !response.ok) {
      throw new Error('Failed to update legal document');
    }

    return response.json();
  },
};

export default legalApi;

